<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignProcessCardsGrid from "~/components/molecules/WrDesignProcessCardsGrid.vue";
import WrDesignReadyToElevateYourBrand from "~/components/molecules/WrDesignReadyToElevateYourBrand.vue";
import WrDesignComparisonSection from "~/components/molecules/WrDesignComparisonSection.vue";
import WrDesignServiceCardsSection from "~/components/molecules/WrDesignServiceCardsSection.vue";
import WrStepper from "~/components/molecules/WrStepper.vue";
import WrDesignFourCardTransformGridSection from "~/components/molecules/WrDesignFourCardTransformGridSection.vue";
import WrDesignIconNumberHeadingCards from "~/components/molecules/WrDesignIconNumberHeadingCards.vue";
import WrDesignRealWorldApplicationsSection from "~/components/molecules/WrDesignRealWorldApplicationsSection.vue";
import WrDesignBenefitCards from "~/components/molecules/WrDesignBenefitCards.vue";
import WrDesignIconLineDecorativeSquaresCards from "~/components/molecules/WrDesignIconLineDecorativeSquaresCards.vue";
import WrDesignFaqsSection from "~/components/molecules/WrDesignFaqsSection.vue";
import WrDesignStatsCard from "~/components/molecules/WrDesignStatsCard.vue";
import WrTestimonialCardCarousel from "~/components/molecules/WrTestimonialCardCarousel.vue";
import WrIcon from "~/components/global/WrIcon.vue";
import WrGradientText from "~/components/global/WrGradientText.vue";
import WrButton from "~/components/global/WrButton.vue";

const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const openModal = (): void => {
  visible.value = true;
};

const colorMode = useColorMode();
const isLight = computed<boolean>(() => colorMode.value !== "dark");
const { isMobile } = useDevice();

/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/services/frameworks/express-development-services-content.json`,
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  problemSolution,
  capabilities,
  process: processData,
  techStack,
  integrationCards: integrationCardsData,
  useCases,
  whyChooseUs,
  cta,
  faq,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  link: [{ rel: "canonical", href: meta_tags.canonical }],
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://webreinvent.com/services" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogImage: meta_tags.meta.find((m: any) => m.property === 'og:image')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
  twitterSite: '@webreinvent',
  ogSiteName: 'WebReinvent',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// ProcessCardsGrid: { number, title, desc, description, svgIcon, rawDesc }
const quickHitCards = computed(() =>
  quickHits.items.map((item: any, i: number) => ({
    number: `0${i + 1}`,
    title: item.title,
    desc: item.description,
    description: item.description,
    rawDesc: item.description,
    svgIcon: item.svgIcon,
  }))
);

// ReadyToElevateYourBrand: { title, description, svgIcon } — 4 items
const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// ComparisonSection: leftCards & rightCards — 4 each → 8 total (4+4)
const comparisonLeftCards = computed(() =>
  problemSolution.leftCards.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);
const comparisonRightCards = computed(() =>
  problemSolution.rightCards.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// ServiceCardsSection: { svgIcon, title, desc } — 6 items
const capabilitiesCards = computed(() =>
  capabilities.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.description,
  }))
);

// WrStepper: uses steps directly from JSON (12 steps with images)
const stepperSteps = computed(() => processData.steps);

// FourCardTransformGrid: { svgIcon, title, description } — 4 items
const techStackCards = computed(() =>
  techStack.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// IconNumberHeadingCards: { number, title, desc, description, svgIcon } — 4 items
const integrationCardItems = computed(() =>
  integrationCardsData.items.map((card: any) => ({
    number: card.number,
    title: card.title,
    desc: card.description,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// RealWorldApplications: { number, title, description, svgIcon } — 4 items
const useCaseCards = computed(() =>
  useCases.items.map((card: any) => ({
    number: card.number,
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// BenefitCards: { svgIcon, title, description } — 4 items
const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// IconLineDecorativeSquaresCards: { number, title, description, svgIcon } — 4 items
const ctaCards = computed(() =>
  cta.cards.map((card: any) => ({
    number: card.number,
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

const faqItems = computed(() => faq.faqItems);

const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Quick Hits (6 cards grid with icons) -->
      <section aria-label="Express Development Capabilities">
        <WrDesignProcessCardsGrid
          :heading="quickHits.heading"
          :subcopy="quickHits.subcopy"
          :cards="quickHitCards"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in quickHitCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon
              :name="card.svgIcon"
              class="size-6 sm:size-8"
              :class="isLight ? 'text-[#501D71]' : 'text-white'"
            />
          </template>
        </WrDesignProcessCardsGrid>
      </section>

      <!-- Section 3: Business Outcomes (4 items with icons) -->
      <section aria-label="Business Outcomes">
        <WrDesignReadyToElevateYourBrand
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
        />
      </section>

      <!-- Section 4: Why Express Matters — Comparison (4+4 cards) -->
      <section aria-label="Why Express Matters">
        <WrDesignComparisonSection
          :heading="problemSolution.heading"
          :subtitle="problemSolution.subtitle"
          :left-title="problemSolution.leftTitle"
          :left-cards="comparisonLeftCards"
          :right-title="problemSolution.rightTitle"
          :right-cards="comparisonRightCards"
          :cta-text="problemSolution.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities (6 service cards with left border) -->
      <section aria-label="Express Development Services">
        <WrDesignServiceCardsSection
          :heading-line-1="capabilities.headingLine1"
          :heading-line-2="capabilities.headingLine2"
          :subcopy="capabilities.subcopy"
          :cards="capabilitiesCards"
          :cta-text="capabilities.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in capabilitiesCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-6 sm:size-8 text-[#501D71] dark:text-[#CDA0FE]" />
          </template>
        </WrDesignServiceCardsSection>
      </section>

      <!-- Section 6: How It Works — Timeline Stepper (12 steps) -->
      <section class="dark:bg-none section-spacing">
        <div class="container-wrapper-xl">
          <h2 class="lg:text-center">
            <WrGradientText>
              {{ processData.heading }}
            </WrGradientText>
          </h2>
          <p
            class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px] mb-10"
          >
            {{ processData.subheading }}
          </p>
          <WrStepper
            :steps="stepperSteps"
            :layout="isMobile ? 'mobile' : 'desktop'"
          />
        </div>
      </section>

      <!-- Section 7: Tech Stack (2x2 crosshairs) — 4 items -->
      <section aria-label="Technology Stack">
        <WrDesignFourCardTransformGridSection
          :heading="techStack.heading"
          :subtitle="techStack.subtitle"
          :cards="techStackCards"
          :cta-text="techStack.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 8: Integration Cards (numbered, dividers) — 4 items -->
      <section aria-label="Integrations and Connectivity">
        <WrDesignIconNumberHeadingCards
          :heading="integrationCardsData.heading"
          :subcopy="integrationCardsData.subcopy"
          :cards="integrationCardItems"
          :cta-text="integrationCardsData.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in integrationCardItems" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-8 max-sm:size-6 text-[#501D71] dark:text-white" />
          </template>
        </WrDesignIconNumberHeadingCards>
      </section>

      <!-- Section 9: Real-World Use Cases (4-col numbered grid) — 4 items -->
      <section aria-label="Real-World Express Use Cases">
        <WrDesignRealWorldApplicationsSection
          :single-line-heading="useCases.heading"
          :subtitle="useCases.subcopy"
          :cards="useCaseCards"
          :cta-text="useCases.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 10: Why Choose Us (2-col hover) — 4 items -->
      <section aria-label="Why Choose WebReinvent">
        <WrDesignBenefitCards
          :heading="whyChooseUs.heading"
          :subcopy="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
        />
      </section>

      <!-- Section 11: CTA Block (vertical list, decorative lines) — 4 items -->
      <section aria-label="Call to Action">
        <WrDesignIconLineDecorativeSquaresCards
          :heading="cta.heading"
          :subcopy="cta.subcopy"
          :cards="ctaCards"
          :cta-text="cta.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in ctaCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-8 max-sm:size-6 text-[#501D71] dark:text-[#CDA0FE]" />
          </template>
        </WrDesignIconLineDecorativeSquaresCards>
      </section>

      <!-- Section 12: Testimonials -->
      <section class="relative mt-16 overflow-hidden max-w-screen-xl mx-auto px-4 z-20">
        <h2 class="text-center mb-11">
          <WrGradientText text="Trusted for excellence and efficiency" />
          <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
            WebReinvent is trusted by leading organisations to enhance their
            development and operational efficiency.
          </p>
        </h2>
        <WrTestimonialCardCarousel />
        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">
          View All
        </WrButton>
      </section>

      <!-- Section 13: Stats Card -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <WrDesignStatsCard />
      </section>

      <!-- Section 14: FAQ (Filtered accordion with tags) — 9 items -->
      <section aria-label="Frequently asked questions">
        <WrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>
    </div>
  </main>

  <WrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" />
</template>
